import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { apiService } from '../services/api';
import { ExistingCustomer } from '../types';

type ServiceKey = 'lawnMowingTrimming' | 'weedControlFertilizing' | 'snowRemoval';

const SERVICES: { key: ServiceKey; label: string; defaultFrequency: string }[] = [
  { key: 'lawnMowingTrimming', label: 'Lawn Mowing', defaultFrequency: 'weekly' },
  { key: 'weedControlFertilizing', label: 'Fertilizing & Weed Control', defaultFrequency: 'monthly' },
  { key: 'snowRemoval', label: 'Snow Removal', defaultFrequency: 'per-instance' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per-instance', label: 'Per visit' },
];

interface Props {
  customer: ExistingCustomer | null;
  onClose: () => void;
  onSaved: (customer: ExistingCustomer) => void;
}

interface Row {
  price: string;
  frequency: string;
  isSeasonal: boolean;
}

const SetCustomerPricesDialog: React.FC<Props> = ({ customer, onClose, onSaved }) => {
  const [rows, setRows] = useState<Partial<Record<ServiceKey, Row>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = SERVICES.filter((s) => customer?.services?.[s.key]);

  useEffect(() => {
    if (!customer) return;
    const next: Partial<Record<ServiceKey, Row>> = {};
    SERVICES.forEach(({ key, defaultFrequency }) => {
      if (!customer.services?.[key]) return;
      const current: any = customer.servicePricing?.[key] || {};
      const isSeasonal = current.frequency === 'seasonal-contract' || !!current.isSeasonalContract;
      const amount = Number(current.price) || Number(current.seasonalContractDetails?.totalContractPrice) || 0;
      next[key] = {
        price: amount > 0 ? String(amount) : '',
        frequency: isSeasonal ? 'seasonal-contract' : current.frequency || defaultFrequency,
        isSeasonal,
      };
    });
    setRows(next);
    setError(null);
  }, [customer]);

  const updateRow = (key: ServiceKey, patch: Partial<Row>) =>
    setRows((prev) => ({ ...prev, [key]: { ...(prev[key] as Row), ...patch } }));

  const handleSave = async () => {
    if (!customer) return;
    const missing = selected.filter(({ key }) => !(parseFloat(rows[key]?.price || '') > 0));
    if (missing.length > 0) {
      setError(`Enter a price greater than $0 for: ${missing.map((s) => s.label).join(', ')}.`);
      return;
    }

    const servicePricing: Record<string, { price: number; frequency: string }> = {};
    selected.forEach(({ key }) => {
      const row = rows[key] as Row;
      servicePricing[key] = { price: parseFloat(row.price), frequency: row.frequency };
    });

    try {
      setSaving(true);
      setError(null);
      const res = await apiService.patch<{ customer: ExistingCustomer }>(
        `/existing-customers/${customer.id || customer._id}/pricing`,
        { servicePricing }
      );
      if (res.success) {
        onSaved(customer);
      } else {
        setError(res.error || 'Failed to save prices');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!customer} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Set Prices{customer ? ` for ${customer.firstName} ${customer.lastName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every service needs a price greater than $0 before you can invite this customer. They will see these
          prices when they accept.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {selected.map(({ key, label }) => {
          const row = rows[key];
          if (!row) return null;
          return (
            <Box key={key} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Typography sx={{ flex: 1, minWidth: 140 }}>{label}</Typography>
              <TextField
                label={row.isSeasonal ? 'Total contract price' : 'Price per service'}
                type="number"
                size="small"
                value={row.price}
                onChange={(e) => updateRow(key, { price: e.target.value })}
                inputProps={{ min: 0.01, step: 0.01 }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ width: 170 }}
              />
              {row.isSeasonal ? (
                <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                  Seasonal contract
                </Typography>
              ) : (
                <FormControl size="small" sx={{ width: 140 }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    label="Frequency"
                    value={row.frequency}
                    onChange={(e) => updateRow(key, { frequency: String(e.target.value) })}
                  >
                    {FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save Prices
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetCustomerPricesDialog;
